import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, Wind, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { setAdminToken } from '../utils/adminAuth';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminLogin = () => {
  const navigate = useNavigate();
  const [apiKey, setApiKey] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Test the API key by trying to fetch orders
      const response = await axios.get(`${API_URL}/api/orders?limit=1`, {
        headers: {
          'Authorization': `Bearer ${apiKey}`
        }
      });

      if (response.status === 200) {
        // Key is valid, store it
        setAdminToken(apiKey);
        navigate('/admin/dashboard');
      }
    } catch (err) {
      console.error('Login error:', err);
      if (err.response?.status === 401 || err.response?.status === 403) {
        setError('Invalid API key. Please check and try again.');
      } else {
        setError('Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-dark-primary flex items-center justify-center p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <motion.div
            className="inline-flex items-center gap-3 mb-4"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring' }}
          >
            <Wind className="w-12 h-12 text-gold" />
            <h1 className="text-4xl font-bold text-gold">MADTOYS</h1>
          </motion.div>
          <p className="text-gray-400 text-lg">Admin Dashboard</p>
        </div>

        <Card className="bg-dark-secondary border-gold/20">
          <CardHeader className="text-center pb-6">
            <div className="w-16 h-16 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-gold" />
            </div>
            <h2 className="text-2xl font-bold text-white">Secure Login</h2>
            <p className="text-gray-400">Enter your admin API key</p>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleLogin} className="space-y-6">
              <div>
                <Label htmlFor="apiKey" className="text-gray-300">Admin API Key</Label>
                <Input
                  id="apiKey"
                  type="password"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder="Enter your API key"
                  className="bg-dark-primary border-gold/20 text-white mt-2"
                  required
                />
              </div>

              {error && (
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="flex items-center gap-2 text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg p-3"
                >
                  <AlertCircle className="w-5 h-5" />
                  <span className="text-sm">{error}</span>
                </motion.div>
              )}

              <Button
                type="submit"
                disabled={loading || !apiKey}
                className="w-full bg-gold hover:bg-gold-dark text-dark-primary font-bold py-6"
              >
                {loading ? 'Verifying...' : 'Login to Dashboard'}
              </Button>

              <div className="text-center text-sm text-gray-500">
                <p>API key is stored locally and never transmitted to third parties.</p>
              </div>
            </form>
          </CardContent>
        </Card>

        <div className="text-center mt-6">
          <button
            onClick={() => navigate('/')}
            className="text-gray-400 hover:text-gold transition-colors text-sm"
          >
            ← Back to Homepage
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default AdminLogin;
